import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import translate from '../../libs/i18n';
import roundToPrecision from '../../utils/roundToPrecision';
import toTitleCase from '../../utils/toTitleCase';

import { HistogramStyle } from './HistogramStyle';

const HISTOGRAM_GRADES = {
  barWidth: 4,
  roundingFactor: 10,
  gradesQuantity: {
    x: 4,
    y: 7,
  },
};
const DEFAULT_HISTOGRAM_HEIGHT = 344;
const DEFAULT_HISTOGRAM_WIDTH = {
  small: 324,
  medium: 576,
  large: 1328,
};

const Histogram = ({ color, data, maxX, maxY, minX, minY, size }) => {
  const language = useSelector((state) => state.settings.language);

  const [gradesX, setGradesX] = useState([]);
  const [gradesY, setGradesY] = useState([]);
  const [height, setHeight] = useState(DEFAULT_HISTOGRAM_HEIGHT);
  const [polygonPoints, setPolygonPoints] = useState('');
  const [width, setWidth] = useState(DEFAULT_HISTOGRAM_WIDTH.large);

  const histogramWidth = useMemo(() => {
    if (size && size.length) {
      return DEFAULT_HISTOGRAM_WIDTH[size];
    }

    return DEFAULT_HISTOGRAM_WIDTH.large;
  }, [size]);

  useEffect(() => {
    if (data && data.length) {
      const currentInitialGap = 0;
      const currentWidth = data.length * 4 + currentInitialGap;
      const currentHeight =
        currentWidth * (DEFAULT_HISTOGRAM_HEIGHT / histogramWidth);

      const initialPoint = `${currentInitialGap} ${currentHeight}`;
      const currentPolygonPoints = [initialPoint];

      data.forEach((d, index) => {
        const x1 = roundToPrecision(
          currentInitialGap + HISTOGRAM_GRADES.barWidth * index,
          2
        );
        const y1 = roundToPrecision(currentHeight * (1 - d / (maxY || 1)), 2);
        const x2 = roundToPrecision(
          currentInitialGap + HISTOGRAM_GRADES.barWidth * (index + 1),
          2
        );
        const y2 = roundToPrecision(currentHeight * (1 - d / (maxY || 1)), 2);

        currentPolygonPoints.push(`${x1} ${y1}`, `${x2} ${y2}`);

        if (index === data.length - 1) {
          currentPolygonPoints.push(`${x2} ${currentHeight}`, initialPoint);
        }
      });

      setPolygonPoints(currentPolygonPoints.join(' '));
      setHeight(currentHeight);
      setWidth(currentWidth);
    }
  }, [data, histogramWidth, maxY]);

  useEffect(() => {
    const sizeX = maxX - minX;
    const stepX = sizeX / (HISTOGRAM_GRADES.gradesQuantity.x - 1);
    const sizeY = maxY - minY;
    const stepY = sizeY / 6;
    const currentGradesX = [];
    const currentGradesY = [];

    for (let i = 0; i < HISTOGRAM_GRADES.gradesQuantity.x; i += 1) {
      currentGradesX.push({
        position: (100 / (HISTOGRAM_GRADES.gradesQuantity.x - 1)) * i,
        value:
          Math.ceil((minX + stepX * i) / HISTOGRAM_GRADES.roundingFactor) *
          HISTOGRAM_GRADES.roundingFactor,
      });
    }

    if (
      minX < 0 &&
      maxX > 0 &&
      !currentGradesX.some((gradeX) => gradeX.value === 0)
    ) {
      currentGradesX.push({
        value: 0,
        position: 100 * (Math.abs(minX) / sizeX),
      });
      currentGradesX.sort((a, b) => a.value - b.value);

      const grade0IndexOf = currentGradesX.findIndex(
        (gradeX) => gradeX.value === 0
      );

      if (
        grade0IndexOf > 0 &&
        grade0IndexOf < HISTOGRAM_GRADES.gradesQuantity.x - 1
      ) {
        if (
          currentGradesX[grade0IndexOf - 1] + stepX / 4 > 0 ||
          currentGradesX[grade0IndexOf + 1] + stepX / 4 < 0
        ) {
          currentGradesX.splice(grade0IndexOf, 1);
        }
      }
    }

    for (let i = 0; i < HISTOGRAM_GRADES.gradesQuantity.y; i += 1) {
      currentGradesY.push({
        position: (100 / (HISTOGRAM_GRADES.gradesQuantity.y - 1)) * i,
        value:
          Math.ceil((minY + stepY * i) / HISTOGRAM_GRADES.roundingFactor) *
          HISTOGRAM_GRADES.roundingFactor,
      });
    }

    setGradesX(currentGradesX);
    setGradesY(currentGradesY);
  }, [minX, maxX, minY, maxY]);

  return (
    <HistogramStyle>
      <div className="histogram-container">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox={`0 0 ${width} ${height}`}
        >
          <g fill={color}>
            <polygon points={polygonPoints} />
          </g>
        </svg>
        {gradesX.map((gradeX, index) => (
          <p
            key={`${gradeX.value}-${gradeX.position}`}
            className="chart-grades grade-x"
            style={{
              left: `calc(${gradeX.position}% + ${
                index < HISTOGRAM_GRADES.gradesQuantity.x - 1 ? 16 : 0
              }px)`,
            }}
          >
            {gradeX.value}
          </p>
        ))}
        {gradesY.map((gradeY, index) => (
          <p
            key={`${gradeY.value}-${gradeY.position}`}
            className={`chart-grades grade-y ${
              index === 0 ? ' first-grade-y' : ''
            }`}
            style={{ bottom: `${gradeY.position}%` }}
          >
            {gradeY.value}
          </p>
        ))}
        <p className="chart-label label-x">{`${toTitleCase(
          translate('density', language)
        )} (HU)`}</p>
        {/* <p className="chart-label label-y">Volume (mL)</p> */}
        <p className="chart-label label-y">
          {toTitleCase(translate('frequency', language))}
        </p>
      </div>
    </HistogramStyle>
  );
};

Histogram.propTypes = {
  color: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.number),
  maxX: PropTypes.number,
  maxY: PropTypes.number,
  minX: PropTypes.number,
  minY: PropTypes.number,
  size: PropTypes.oneOf(Object.keys(DEFAULT_HISTOGRAM_WIDTH)),
};

Histogram.defaultProps = {
  color: '#000',
  data: [],
  maxX: 100,
  maxY: 100,
  minX: 0,
  minY: 0,
  size: 'large',
};

export default Histogram;
